import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ShoppingCart as ShoppingCartIcon } from "../../icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebase";
import StoresAppBar from "./StoresAppBar";
import DiscountPriceBreakdown from "../../components/DiscountPriceBreakdown";
import ProductMediaGallery from "../../components/ProductMediaGallery";
import { T } from "../Translate";
import { pageContentSx } from "../../pageLayout";

const formatPrice = (value) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const ProductDetail = () => {
  const { storeSlug, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [storeData, setStoreData] = useState(
    location.state?.storeName ? { storeName: location.state.storeName } : null
  );
  const [product, setProduct] = useState(
    location.state?.productName
      ? {
          name: location.state.productName,
          price: location.state.price,
          imageUrl: location.state.imageUrl,
          images: location.state.images?.length ? location.state.images : (location.state.imageUrl ? [location.state.imageUrl] : []),
          videoUrl: location.state.videoUrl || null,
          description: location.state.description,
          couponCode: location.state.couponCode || null,
          couponDiscount: location.state.couponDiscount || null,
          stock: location.state.stock ?? null,
        }
      : null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const storeRef = db.collection("stores").doc(storeSlug);
        const [storeSnapshot, productSnapshot] = await Promise.all([
          storeRef.get(),
          storeRef.collection("products").doc(productId).get(),
        ]);

        if (!storeSnapshot.exists || !productSnapshot.exists) {
          if (active) {
            setError(<T>No encontramos este producto. Revisa el enlace o vuelve a la tienda.</T>);
          }
          return;
        }

        if (active) {
          const storeInfo = storeSnapshot.data();
          const productData = productSnapshot.data();
          setStoreData({ storeName: storeInfo.storeName || storeSlug });
          setProduct({
            name: productData.name,
            price: productData.price,
            imageUrl: productData.imageUrl || "",
            images: productData.images?.length ? productData.images : (productData.imageUrl ? [productData.imageUrl] : []),
            videoUrl: productData.videoUrl || null,
            description: productData.description || "",
            couponCode: productData.couponCode || null,
            couponDiscount: productData.couponDiscount || null,
            stock: productData.stock ?? null,
          });
          setError(null);
        }
      } catch (err) {
        console.error("Error al cargar el producto:", err);
        if (active) {
          setError(<T>Ocurrió un error al cargar el producto.</T>);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [storeSlug, productId]);

  const handleBuyClick = () => {
    if (!product) return;

    navigate(`/stores/${storeSlug}/products/${productId}/payment`, {
      state: {
        storeName: storeData?.storeName,
        productName: product.name,
        price: product.price,
        imageUrl: product.imageUrl,
        images: product.images,
        videoUrl: product.videoUrl,
        description: product.description,
      },
    });
  };

  const goBackToStore = () => {
    navigate(`/stores/${storeSlug}`);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar
        title={product?.name || <T>Producto</T>}
        subtitle={storeData?.storeName || "Notiline"}
        quickNav={false}
        maxWidth="lg"
      />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, pt: { xs: 6, md: 10 } }}>
        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {loading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <T>Cargando información del producto...</T>
              </Typography>
            </Stack>
          ) : error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 3, sm: 5 }} alignItems={{ xs: "center", sm: "flex-start" }}>
              <Box sx={{ width: { xs: "100%", sm: "45%" }, flexShrink: 0 }}>
                <ProductMediaGallery images={product?.images} videoUrl={product?.videoUrl} alt={product?.name} />
              </Box>
              <Stack spacing={2} sx={{ width: { xs: "100%", sm: "55%" } }}>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  <T>Producto destacado</T>
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                  {product?.name}
                </Typography>
                {product?.couponCode && product?.couponDiscount ? (
                  <Stack spacing={1}>
                    <DiscountPriceBreakdown
                      originalPrice={Number(product.price)}
                      discountPercent={product.couponDiscount}
                      finalPrice={Number(product.price) * (1 - Number(product.couponDiscount) / 100)}
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      <T>Usa el código <b>{product.couponCode}</b> al pagar para obtener este precio.</T>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatPrice(product?.price)}
                  </Typography>
                )}
                {product?.stock != null && (
                  <Chip
                    size="small"
                    color={product.stock > 0 ? "default" : "error"}
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                    label={product.stock > 0 ? <T>Stock: {product.stock}</T> : <T>Agotado</T>}
                  />
                )}
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {product?.description}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleBuyClick}
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <T>Comprar</T>
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={goBackToStore}
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <T>Ver más productos</T>
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProductDetail;
